/**
 * @fileoverview Public program directory (/programs). No account needed —
 * students and parents browse the programs organizations run on Hans and open
 * a program to book a lesson with one of its instructors.
 *
 * Uses a bare axios instance (like BookingPage): the shared api client's 401
 * interceptor must never redirect a public visitor to the instructor login.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import BACKEND_URL from "@/lib/backendUrl";

const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

const shellClass = "min-h-screen px-6 py-14";
const shellStyle = {
  background:
    "radial-gradient(ellipse at 50% 0%, rgba(244,207,8,0.055) 0%, rgba(244,207,8,0.015) 40%, transparent 70%) no-repeat, #08080a",
};

const ProgramsDirectoryPage = () => {
  const [programs, setPrograms] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/programs")
      .then((res) => setPrograms(res.data.programs || []))
      .catch(() => setError("Could not load programs — try again in a moment."));
  }, []);

  return (
    <div
      className={shellClass}
      style={shellStyle}
      data-theme="dark"
    >
      <div className="mx-auto max-w-2xl" data-testid="programs-directory">
        <div className="mb-10 text-center">
          <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.08em] text-[#f4cf08]">
            Find a program
          </p>
          <h1 className="mt-2 font-serif text-[34px] font-normal tracking-[-0.01em] text-white">Music programs</h1>
          <p className="mx-auto mt-3 max-w-md font-sans text-sm leading-relaxed text-zinc-400">
            Programs run by music organizations on Hans. Open one to meet its instructors and book a lesson.
          </p>
        </div>

        {error && (
          <p className="text-center font-sans text-base text-zinc-400" data-testid="programs-error">
            {error}
          </p>
        )}
        {!error && programs === null && <p className="text-center font-sans text-sm text-zinc-500">Loading…</p>}
        {programs?.length === 0 && (
          <p className="text-center font-sans text-sm text-zinc-500" data-testid="programs-empty">
            No programs are open right now — check back soon.
          </p>
        )}

        <div className="space-y-4">
          {(programs || []).map((p) => (
            <Link
              key={p.program_id}
              to={`/programs/${p.program_id}`}
              data-testid={`program-card-${p.program_id}`}
              className="block rounded-2xl border border-white/10 bg-zinc-900/60 p-6 transition-colors hover:border-[rgba(244,207,8,0.4)]"
            >
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="font-serif text-xl font-normal text-white">{p.title}</h2>
                <span className="shrink-0 font-sans text-[12px] text-zinc-500">
                  {p.instructor_count} instructor{p.instructor_count === 1 ? "" : "s"}
                </span>
              </div>
              <p className="mt-0.5 font-sans text-[13px] font-medium text-[#f4cf08]/80">{p.org_name}</p>
              {p.description && (
                <p className="mt-2 line-clamp-3 font-sans text-sm leading-relaxed text-zinc-400">{p.description}</p>
              )}
              <p className="mt-2 font-sans text-[13px] text-zinc-500">
                {[(p.instruments || []).join(", "), p.location, p.start_date ? `Starts ${p.start_date}` : null]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgramsDirectoryPage;
